import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  signInWithCustomToken,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, getDocs, collection, query, where, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { isInWebView, requestNativeSocialSignIn } from '../bridge/webBridge';
import { getAllTerms } from './termsService';
import { recordRequiredConsents } from './consentService';

const CF_BASE = 'https://us-central1-ieum-8fd99.cloudfunctions.net';

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

const PASSWORD_ERROR_CODES = new Set([
  'auth/invalid-credential',
  'auth/user-not-found',
  'auth/wrong-password',
]);

const signInWithCompanyAlias = async (email, password) => {
  const response = await fetch(`${CF_BASE}/loginAdminAlias`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.token) {
    const error = new Error(payload.error || '로그인 정보를 확인해 주세요.');
    error.code = payload.code || 'auth/invalid-credential';
    throw error;
  }
  return signInWithCustomToken(auth, payload.token);
};

/**
 * 로그인 — Firebase Auth + Firestore users/{uid} 조회
 */
export const login = async (email, password) => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  let cred;
  try {
    cred = await signInWithEmailAndPassword(auth, normalizedEmail, password);
  } catch (error) {
    const isCompanyAlias = normalizedEmail.endsWith('@ieum.in');
    if (!isCompanyAlias || !PASSWORD_ERROR_CODES.has(error.code)) throw error;
    cred = await signInWithCompanyAlias(normalizedEmail, password);
  }
  const uid = cred.user.uid;

  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) {
    throw new Error('사용자 정보를 찾을 수 없습니다.');
  }

  const data = snap.data();
  if (data.status === 'pending') {
    await signOut(auth);
    const err = new Error('관리자 승인 대기중입니다.');
    err.code = 'auth/pending';
    err.autoVerification = {
      status: data.auto_verification_status || null,
      reasonCode: data.auto_verification_reason_code || null,
      reasonText: data.auto_verification_reason_text || null,
    };
    throw err;
  }
  if (data.status === 'withdrawn') {
    await signOut(auth);
    const err = new Error('탈퇴 처리된 계정입니다. 재가입이 불가합니다.');
    err.code = 'auth/withdrawn';
    throw err;
  }
  if (data.status === 'suspended') {
    await signOut(auth);
    const err = new Error('계정이 정지되었습니다. 관리자에게 문의하세요.');
    err.code = 'auth/suspended';
    throw err;
  }
  return { uid, ...data };
};

/**
 * 회원가입 — Firebase Auth 계정 생성 + Firestore 문서 생성
 *
 * verifiedToken이 전달되면(OTP 인증 완료) 기존 계정 탐색을 먼저 수행.
 * 동일 전화번호의 기존 계정(소셜/이메일)이 있으면 가입을 차단하고 로그인 안내 에러를 던짐.
 */
export const signup = async ({ email, password, name, phone, businessName, role = 'realtor', verifiedToken, profile = {}, tempPassword = false, agreedTermKeys = [] }) => {
  const normPhone = (phone || '').replace(/\D/g, '');

  // 1) OTP 인증 완료된 경우 — CF로 기존 계정 조회 (전화번호 마스터 키)
  if (verifiedToken && normPhone) {
    try {
      const lookup = await lookupUserByVerifiedPhone({
        phone: normPhone,
        verifiedToken,
        socialUid: '',
        provider: 'email',
      });
      if (lookup?.found && lookup?.user) {
        const existing = lookup.user;
        const existingProvider =
          existing.linkedSocialProvider ||
          existing.provider ||
          (existing.linkedSocialUid ? 'social' : 'email');
        const providerLabel = ({
          google: '구글',
          kakao: '카카오',
          apple: '애플',
          email: '이메일/비밀번호',
          social: '소셜',
        })[existingProvider] || '다른 로그인 방법';
        const err = new Error(`이미 ${providerLabel}(으)로 가입된 계정이 있습니다.\n기존 방법으로 로그인 후 사용해 주세요.`);
        err.code = 'auth/phone-already-linked';
        err.existingProvider = existingProvider;
        throw err;
      }
    } catch (e) {
      if (e?.code === 'auth/phone-already-linked') throw e;
      // CF lookup 자체가 실패한 경우(네트워크/권한) → 아래 Firestore 쿼리 fallback로 검증
    }
  }

  // 2) Firestore 직접 조회로 전화번호 중복 체크 (OTP 인증 여부 무관 — 빈틈 메우기)
  //    이전엔 OTP 인증 시 lookup CF 에만 위임했는데, 검사 범위 빈틈이 있어 중복 가입이 발생할 수 있어
  //    가입 직전 users.phone 을 직접 한 번 더 검사한다.
  if (normPhone) {
    const phoneQuery = query(collection(db, 'users'), where('phone', '==', normPhone));
    const phoneSnap = await getDocs(phoneQuery);
    if (!phoneSnap.empty) {
      const existing = phoneSnap.docs[0].data();
      if (existing.status === 'withdrawn') {
        const err = new Error('탈퇴된 계정입니다. 재가입이 불가합니다.');
        err.code = 'auth/withdrawn';
        throw err;
      }
      const err = new Error('이미 가입된 전화번호입니다.');
      err.code = 'auth/phone-already-in-use';
      throw err;
    }
  }

  // 3) 이메일 사전 탈퇴 체크 — Firebase Auth 호출 전에 차단
  if (email) {
    const emailQuery = query(collection(db, 'users'), where('email', '==', email));
    const emailSnap = await getDocs(emailQuery);
    if (!emailSnap.empty && emailSnap.docs[0].data().status === 'withdrawn') {
      const err = new Error('탈퇴된 계정입니다. 재가입이 불가합니다.');
      err.code = 'auth/withdrawn';
      throw err;
    }
  }

  // 4) 제휴업체 — 담당자 전화번호 중복 체크 (대표자 != 담당자일 때만)
  // users.phone(다른 가입자의 대표폰)과 partners.manager_phone(다른 가입자의 담당자폰) 양쪽 확인
  if (role === 'partner') {
    const managerPhone = (profile?.manager_phone || '').replace(/\D/g, '');
    if (managerPhone && managerPhone !== normPhone) {
      const dupRepQuery = query(collection(db, 'users'), where('phone', '==', managerPhone));
      const dupRepSnap = await getDocs(dupRepQuery);
      if (!dupRepSnap.empty) {
        const err = new Error('이미 가입된 연락처입니다.');
        err.code = 'auth/manager-phone-already-in-use';
        throw err;
      }
      const dupMgrQuery = query(collection(db, 'partners'), where('manager_phone', '==', managerPhone));
      const dupMgrSnap = await getDocs(dupMgrQuery);
      if (!dupMgrSnap.empty) {
        const err = new Error('이미 가입된 연락처입니다.');
        err.code = 'auth/manager-phone-already-in-use';
        throw err;
      }
    }
    // 사업자등록번호 중복 체크 — 제휴업체는 1회선만 (공인중개사는 적용 안 함 / 다회선 허용)
    const bizNoDigits = String(profile?.business_number || profile?.businessNumber || '').replace(/\D/g, '');
    if (bizNoDigits) {
      const dupBizSnake = await getDocs(query(collection(db, 'partners'), where('business_number', '==', bizNoDigits)));
      const dupBizCamel = dupBizSnake.empty
        ? await getDocs(query(collection(db, 'partners'), where('businessNumber', '==', bizNoDigits)))
        : { empty: true };
      if (!dupBizSnake.empty || !dupBizCamel.empty) {
        const err = new Error('이미 가입된 사업자등록번호입니다.');
        err.code = 'partner/business-number-already-in-use';
        throw err;
      }
    }
  }

  const cred = await createUserWithEmailAndPassword(auth, email, password);
  const uid = cred.user.uid;

  // users 문서 생성
  // 공인중개사는 자동 승인(active), 제휴업체는 관리자 승인 필요(pending)
  const initialStatus = role === 'realtor' ? 'active' : 'pending';
  await setDoc(doc(db, 'users', uid), {
    email,
    name,
    phone: normPhone,
    role,
    status: initialStatus,
    provider: 'email',
    phoneVerified: !!verifiedToken,
    tempPassword: !!tempPassword,
    referrer_id: profile?.referrer_id || '',
    referrer_at: profile?.referrer_id ? serverTimestamp() : null,
    createdAt: serverTimestamp(),
  });

  // 역할별 상세 문서
  if (role === 'realtor') {
    await setDoc(doc(db, 'realtors', uid), {
      businessName: businessName || '',
      qrCodeUrl: '',
      referrer_id: '',
      ...profile,
      createdAt: serverTimestamp(),
    });
  } else if (role === 'partner') {
    await setDoc(doc(db, 'partners', uid), {
      businessName: businessName || '',
      ...profile,
      createdAt: serverTimestamp(),
    });
  }

  // 약관 동의 이력 저장 — 접속 IP 캡처를 위해 서버 함수(recordConsentWithIp) 경유
  //  · 클라이언트 직접 저장 방식은 접속 IP 를 알 수 없어 ip 필드가 비어 저장됐음
  //  · 서버 함수가 x-forwarded-for 에서 IP 를 캡처 + terms_agreements_log 저장 + users/role 머지까지 처리
  if (Array.isArray(agreedTermKeys) && agreedTermKeys.length > 0) {
    try {
      const termsByKey = await getAllTerms();
      const terms = agreedTermKeys
        .map((key) => {
          const t = termsByKey[key];
          if (!t) return null;
          return {
            key,
            title: t.title || '',
            version: t.version || '',
            effectiveDate: t.effective_date || '',
            content: t.content || '',
          };
        })
        .filter(Boolean);
      if (terms.length > 0) {
        await recordRequiredConsents({
          user: auth.currentUser,
          profile: {
            uid,
            name: profile.representative_name || profile.business_name || name || '',
            business_name: profile.business_name || businessName || '',
            email,
            phone: normPhone,
            role,
          },
          role,
          terms,
          method: 'signup',
          path: 'signup',
        });
      }
    } catch (consentErr) {
      console.error('약관 동의 이력 저장 실패:', consentErr);
      // 동의 이력 실패는 가입 자체를 막지 않음 (사용자 다음 로그인 시 /consent로 안내됨)
    }
  }

  // 승인 전이므로 즉시 로그아웃
  await signOut(auth);

  return { uid, name, role };
};

/**
 * 로그아웃
 */
export const logout = async () => {
  return signOut(auth);
};

/**
 * 비밀번호 찾기 — 이메일+이름+전화번호 일치 시 임시 비밀번호 SMS 발송
 * Cloud Function sendTempPassword를 호출하여:
 *  (1) users 문서의 3개 필드 일치 검증
 *  (2) Auth 비밀번호 업데이트
 *  (3) SMS 발송 + notification_logs 기록
 */
export const findPassword = async ({ email, name, phone }) => {
  const normEmail = (email || '').trim().toLowerCase();
  const normName = (name || '').trim();
  const normPhone = (phone || '').replace(/-/g, '').trim();

  if (!normEmail || !normName || !normPhone) {
    const err = new Error('입력하신 정보가 일치하지 않습니다.');
    err.code = 'findpw/invalid-input';
    throw err;
  }

  const url = `${CF_BASE}/sendTempPassword`;

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normEmail, name: normName, phone: normPhone }),
  });

  const payload = await res.json().catch(() => ({}));
  if (!res.ok || !payload?.ok) {
    const err = new Error(payload?.error || '입력하신 정보가 일치하지 않습니다.');
    err.code = payload?.code || 'findpw/not-matched';
    throw err;
  }
  return payload;
};

/**
 * 회원가입 레거시 호환
 */
export const signupUser = signup;

// ────────────────────────────────────────
// 소셜 로그인
// ────────────────────────────────────────

const SOCIAL_PROVIDERS = {
  google: 'google',
  apple: 'apple',
  kakao: 'kakao',
};

/**
 * 소셜 로그인 통합 엔트리
 * - 웹: Google/Apple은 Firebase Popup, Kakao는 JS SDK + Custom Token CF
 * - 반환: { user, authUid, provider, isNewAuth }
 */
export const signInWithSocial = async (provider) => {
  if (!SOCIAL_PROVIDERS[provider]) {
    const err = new Error('지원하지 않는 로그인 방식입니다.');
    err.code = 'social/unsupported';
    throw err;
  }

  let cred;
  // RN WebView 환경이면 네이티브 브릿지 경유 (구글 WebView 정책 우회 + 카카오 OAuth 리다이렉트 차단 우회)
  if (isInWebView() && (provider === 'google' || provider === 'apple' || provider === 'kakao')) {
    const result = await requestNativeSocialSignIn(provider);
    if (provider === 'google') {
      const idToken = result.idToken || result.id_token;
      if (!idToken) {
        const err = new Error('네이티브 구글 로그인에서 idToken 을 받지 못했습니다.');
        err.code = 'social/google-no-id-token';
        throw err;
      }
      const credential = GoogleAuthProvider.credential(idToken);
      cred = await signInWithCredential(auth, credential);
    } else if (provider === 'apple') {
      const identityToken = result.identityToken || result.identity_token;
      const rawNonce = result.nonce || result.rawNonce;
      if (!identityToken) {
        const err = new Error('네이티브 애플 로그인에서 identityToken 을 받지 못했습니다.');
        err.code = 'social/apple-no-id-token';
        throw err;
      }
      const appleCred = new OAuthProvider('apple.com').credential({ idToken: identityToken, rawNonce });
      cred = await signInWithCredential(auth, appleCred);
    } else {
      // kakao: accessToken 을 기존 kakaoCustomToken CF 로 교환 (legacy accessToken 경로)
      const accessToken = result.accessToken || result.access_token;
      if (!accessToken) {
        const err = new Error('네이티브 카카오 로그인에서 accessToken 을 받지 못했습니다.');
        err.code = 'social/kakao-no-access-token';
        throw err;
      }
      const res = await fetch(`${CF_BASE}/kakaoCustomToken`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.customToken) {
        const err = new Error(data?.error || '카카오 로그인에 실패했습니다.');
        err.code = data?.code || 'social/kakao-failed';
        throw err;
      }
      cred = await signInWithCustomToken(auth, data.customToken);
    }
  } else if (provider === 'google') {
    cred = await signInWithPopup(auth, googleProvider);
  } else if (provider === 'apple') {
    cred = await signInWithPopup(auth, appleProvider);
  } else if (provider === 'kakao') {
    // 웹 Kakao 는 JS SDK v2 redirect 방식을 사용하므로 이 함수를 호출하면 안 됨
    const err = new Error('Kakao는 authorizeKakao() 리다이렉트 방식을 사용하세요.');
    err.code = 'social/kakao-use-redirect';
    throw err;
  }

  const user = cred.user;
  try { sessionStorage.setItem('lastSocialProvider', provider); } catch {}

  return {
    user,
    authUid: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    provider,
  };
};

/**
 * 카카오 로그인 시작 (Kakao JS SDK v2 redirect 방식)
 * 사용자가 카카오 로그인 성공 시 redirectUri(/auth/kakao/callback)로 code + state 리턴
 * @param {string} [returnTo] 로그인 성공 후 복귀할 경로(선택)
 */
export const authorizeKakao = (returnTo = '') => {
  if (!window.Kakao || !window.Kakao.Auth || typeof window.Kakao.Auth.authorize !== 'function') {
    const err = new Error('카카오 SDK가 로드되지 않았습니다.');
    err.code = 'social/kakao-sdk-missing';
    throw err;
  }
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
  try {
    sessionStorage.setItem('kakao.oauth.state', state);
    if (returnTo) sessionStorage.setItem('kakao.oauth.returnTo', returnTo);
  } catch {}
  const redirectUri = `${window.location.origin}/auth/kakao/callback`;
  window.Kakao.Auth.authorize({ redirectUri, state });
};

/**
 * 카카오 콜백에서 code → Firebase Custom Token 교환 + 로그인
 * @returns {{authUid, email, displayName, provider:'kakao'}}
 */
export const exchangeKakaoCode = async (code, redirectUri) => {
  const res = await fetch(`${CF_BASE}/kakaoCustomToken`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirectUri }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.customToken) {
    const err = new Error(data?.error || '카카오 로그인에 실패했습니다.');
    err.code = data?.code || 'social/kakao-failed';
    throw err;
  }
  const cred = await signInWithCustomToken(auth, data.customToken);
  try { sessionStorage.setItem('lastSocialProvider', 'kakao'); } catch {}
  return {
    user: cred.user,
    authUid: cred.user.uid,
    email: data.email || cred.user.email || '',
    displayName: data.nickname || cred.user.displayName || '',
    provider: 'kakao',
  };
};

/**
 * 소셜 로그인 성공 직후 공통 후처리 — 기존 계정 발견 여부에 따라 분기 결과를 반환
 *
 * @returns {Promise<{kind:'home'|'link-phone'|'alert', alert?:{type,title,message}, state?:object}>}
 *   - kind='home': 정상 사용자 → 홈으로 리다이렉트 (refreshSession 호출은 caller 책임)
 *   - kind='link-phone': 신규 소셜 → /social/link-phone (전화번호 OTP → 통합 또는 신규)
 *   - kind='alert': pending/suspended → signOut 후 alert 표시
 */
export const processSocialSignInResult = async ({ authUid, email, displayName, provider }) => {
  const lookup = await findUserByAuth(authUid);
  if (lookup.found) {
    const user = lookup.user;
    if (user.status === 'pending') {
      await signOut(auth);
      return {
        kind: 'alert',
        alert: { type: 'info', title: '이음에서 승인을 검토 중입니다', message: '관리자 승인 후 로그인하실 수 있습니다.' },
      };
    }
    if (user.status === 'suspended') {
      await signOut(auth);
      return {
        kind: 'alert',
        alert: { type: 'error', title: '계정이 정지되었습니다', message: '관리자에게 문의하세요.' },
      };
    }
    return { kind: 'home', user };
  }
  return {
    kind: 'link-phone',
    state: { authUid, email, displayName, provider },
  };
};

/**
 * 소셜 로그인 이후 users/{authUid} 조회 + linkedSocialUid 역탐색
 * 반환: { found: boolean, user?: {...} }
 */
export const findUserByAuth = async (authUid) => {
  // 1) 직접 문서 조회
  const direct = await getDoc(doc(db, 'users', authUid));
  if (direct.exists()) {
    const data = direct.data();
    // 역할/이름이 있는 정상 문서만 "기존 사용자"로 인정
    if (data.role && data.name) {
      return { found: true, user: { uid: authUid, ...data } };
    }
  }

  // 2) linkedSocialUid 역탐색
  const q = query(collection(db, 'users'), where('linkedSocialUid', '==', authUid));
  const snap = await getDocs(q);
  if (!snap.empty) {
    const d = snap.docs[0];
    return { found: true, user: { uid: d.id, ...d.data() } };
  }

  return { found: false };
};

/**
 * OTP 발급 요청
 * @param {string} phone
 * @param {string} purpose - "social_link" | "profile_change" | "signup" | "generic"
 */
export const requestPhoneOtp = async (phone, purpose = 'generic') => {
  const normPhone = (phone || '').replace(/\D/g, '');
  if (normPhone.length < 10 || normPhone.length > 11) {
    const err = new Error('전화번호 형식이 올바르지 않습니다.');
    err.code = 'otp/invalid-phone';
    throw err;
  }
  const res = await fetch(`${CF_BASE}/requestPhoneOtp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: normPhone, purpose }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok) {
    const err = new Error(data?.error || '인증번호 발송에 실패했습니다.');
    err.code = data?.code || 'otp/request-failed';
    throw err;
  }
  return data;
};

/**
 * OTP 검증
 * 반환: { verifiedToken, verifiedUntil, purpose }
 */
export const verifyPhoneOtp = async (phone, code) => {
  const normPhone = (phone || '').replace(/\D/g, '');
  const normCode = (code || '').replace(/\D/g, '');
  const res = await fetch(`${CF_BASE}/verifyPhoneOtp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: normPhone, code: normCode }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok) {
    const err = new Error(data?.error || '인증번호가 올바르지 않습니다.');
    err.code = data?.code || 'otp/verify-failed';
    err.attemptsLeft = data?.attemptsLeft;
    throw err;
  }
  return data;
};

/**
 * 인증된 전화번호로 기존 users 조회 (read-only)
 * 반환: { found: boolean, user?: {...} }
 */
export const lookupUserByVerifiedPhone = async ({ phone, verifiedToken, socialUid, provider }) => {
  const res = await fetch(`${CF_BASE}/linkSocialToPhone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      phone: (phone || '').replace(/\D/g, ''),
      verifiedToken,
      socialUid,
      provider,
      mode: 'lookup',
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok) {
    const err = new Error(data?.error || '계정 조회에 실패했습니다.');
    err.code = data?.code || 'social/lookup-failed';
    throw err;
  }
  return data; // { found, linked, user? }
};

/**
 * 실제 linkedSocialUid 저장 (사용자 확인 후 호출)
 */
export const linkSocialToPhone = async ({ phone, verifiedToken, socialUid, provider }) => {
  const res = await fetch(`${CF_BASE}/linkSocialToPhone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      phone: (phone || '').replace(/\D/g, ''),
      verifiedToken,
      socialUid,
      provider,
      mode: 'link',
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok) {
    const err = new Error(data?.error || '계정 연결에 실패했습니다.');
    err.code = data?.code || 'social/link-failed';
    throw err;
  }
  return data; // { found, linked, user? }
};

/**
 * 소셜 로그인 후 신규 회원가입 — Firebase Auth 계정은 이미 있음(authUid)
 * users/{authUid} 문서 + 역할별 상세 문서 생성
 */
export const signupSocialUser = async ({ authUid, email, name, phone, businessName, role, provider, verifiedToken, profile = {} }) => {
  if (!authUid) throw new Error('authUid 필수');
  if (!role) throw new Error('role 필수');

  const normPhone = (phone || '').replace(/\D/g, '');

  // 동일 전화번호 중복 체크 (현 authUid 제외)
  // verifiedToken이 있으면 OTP 인증 + lookupUserByVerifiedPhone 단계에서 이미 확인됨 → 스킵
  if (normPhone && !verifiedToken) {
    const phoneQuery = query(collection(db, 'users'), where('phone', '==', normPhone));
    const phoneSnap = await getDocs(phoneQuery);
    const dup = phoneSnap.docs.find((d) => d.id !== authUid);
    if (dup) {
      const err = new Error('이미 가입된 전화번호입니다.');
      err.code = 'auth/phone-already-in-use';
      throw err;
    }
  }

  const initialStatus = role === 'realtor' ? 'active' : 'pending';

  await setDoc(
    doc(db, 'users', authUid),
    {
      email: email || '',
      name: name || '',
      phone: normPhone,
      role,
      status: initialStatus,
      provider: provider || 'email',
      referrer_id: profile?.referrer_id || '',
      referrer_at: profile?.referrer_id ? serverTimestamp() : null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  if (role === 'realtor') {
    await setDoc(
      doc(db, 'realtors', authUid),
      {
        businessName: businessName || '',
        qrCodeUrl: '',
        referrer_id: '',
        ...profile,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  } else if (role === 'partner') {
    await setDoc(
      doc(db, 'partners', authUid),
      {
        businessName: businessName || '',
        ...profile,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  // 제휴업체는 승인 전이므로 즉시 로그아웃
  if (role === 'partner') {
    await signOut(auth);
  }

  return { uid: authUid, role, status: initialStatus };
};

/**
 * users/{uid}에 linkedSocialUid 덮어쓰기 (클라이언트에서 보조적으로 사용; 주 경로는 CF linkSocialToPhone)
 */
export const attachSocialLink = async ({ uid, socialUid, provider }) => {
  await updateDoc(doc(db, 'users', uid), {
    linkedSocialUid: socialUid,
    [`linkedSocialUid_${provider}`]: socialUid,
    linkedSocialProvider: provider,
    linkedSocialAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const getLastSocialProvider = () => {
  try { return sessionStorage.getItem('lastSocialProvider') || ''; } catch { return ''; }
};
